import { ActivatedRoute } from '@angular/router';
import { AdministrationCommonListComponent } from '@admin/components/administration-common-list/administration-common-list.component';
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { ApiAdministrationService } from '@admin/services/api-administration.service';
import { AccountService } from '@admin/services/account.service';
import { StateService } from '@core/services/state/state.service';
import { TranslateService } from '@ngx-translate/core';
@Component({
    selector: 'app-administration-users-list',
    standalone: false,
    imports: [],
    templateUrl: './administration-users-list.component.html',
    styleUrl: './administration-users-list.component.scss'
})
export class AdministrationUsersListComponent extends AdministrationCommonListComponent implements OnDestroy {

    @Input() accountNumber;
    @Input() hasNext = false;

    displayUsers: any[] = [];
    hasNextPage = false;
    loading = false;
    private searchTimer: ReturnType<typeof setTimeout> | null = null;
    private requestToken = 0;
    private previousInputList: any[] | null = null;
    private previousAccountNumber = null;

    constructor(
        protected translate: TranslateService,
        protected route: ActivatedRoute,
        protected stateService: StateService,
        private apiAdministrationService: ApiAdministrationService,
        private accountService: AccountService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super(translate, route, stateService);
    }

    ngOnChanges(): void {
        const normalizedUsers = this.normalizeUsers(this.list);

        if (this.list !== this.previousInputList || this.accountNumber !== this.previousAccountNumber) {
            this.actualPage = 1;
            this.value = '';
            this.previousInputList = this.list;
            this.previousAccountNumber = this.accountNumber;
        }

        this.hasNextPage = this.resolveHasNext(this.hasNext, normalizedUsers.length);
        this.syncDisplayUsers(normalizedUsers);
    }
    
    onUserSearch(value: string): void {
        this.value = value;
        this.actualPage = 1;

        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }

        this.searchTimer = setTimeout(() => {
            this.fetchUsers(1);
        }, 250);
    }

    override goToPage(factor) {
        if (this.loading) {
            return;
        }

        const requestedPage = this.actualPage + factor;
        if (requestedPage < 1) {
            return;
        }

        if (factor > 0 && !this.hasNextPage) {
            return;
        }

        this.fetchUsers(requestedPage);
    }

    ngOnDestroy(): void {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
    }
    
    UsersListCellProperties(columnIndex) {
        let styleObject = {
            width: '',
            vertical_align: '',
            text_align: ''
        };

        switch (columnIndex) {
            case 2:
                styleObject.width = '200px';
                return styleObject;
            case 4:
                styleObject.width = '100px';
                return styleObject;
            default:
                styleObject.width = 'auto';
                return styleObject;
        }
    }

    private fetchUsers(requestedPage = 1): void {
        if (!this.accountNumber) {
            return;
        }

        const requestToken = ++this.requestToken;
        this.loading = true;

        this.apiAdministrationService.users([this.buildUsersParams(requestedPage)]).subscribe(
            res => {
                if (requestToken !== this.requestToken) {
                    return;
                }

                const normalizedUsers = this.normalizeUsers(res?.data ?? res);
                if (requestedPage > 1 && normalizedUsers.length === 0) {
                    this.hasNextPage = false;
                    this.loading = false;
                    this.changeDetectorRef.markForCheck();
                    return;
                }

                this.actualPage = requestedPage;
                this.hasNextPage = this.resolveHasNext(res?.hasNext, normalizedUsers.length);
                this.list = normalizedUsers.slice(0, this.maxPerPage);
                this.syncDisplayUsers(this.list);
                this.loading = false;
                this.changeDetectorRef.markForCheck();
            },
            () => {
                if (requestToken !== this.requestToken) {
                    return;
                }

                this.loading = false;
                this.changeDetectorRef.markForCheck();
            }
        );
    }

    private buildUsersParams(requestedPage: number) {
        const params: any = {
            skip: (requestedPage - 1) * this.maxPerPage,
            limit: this.maxPerPage,
            order: { firstName: 'asc' },
            accountNumber: [this.accountNumber],
            asList: true,
            includeCount: false
        };

        if (this.value) {
            params.search = this.value;
        }

        return params;
    }

    private syncDisplayUsers(users: any[]): void {
        const normalizedUsers = users.map(user => this.decorateUser(user));

        while (normalizedUsers.length < this.maxPerPage) {
            normalizedUsers.push(null);
        }

        this.displayUsers = normalizedUsers;
    }

    private decorateUser(user: any) {
        if (!user) {
            return null;
        }

        if (this.accountService.subAccounts && ('principalSubAccount' in user)) {
            const subAccountById: any = this.accountService.subAccounts.find((subAccount: any) => {
                return subAccount.id === user['principalSubAccount'];
            });
            const none = (this.translate.currentLang === 'fr') ? 'Aucun' : 'None';
            user['principalSubAccountName'] = subAccountById ? subAccountById.name : none;
        }

        return user;
    }

    private normalizeUsers(users: any): any[] {
        if (Array.isArray(users)) {
            return users;
        }

        if (users && typeof users === 'object') {
            return Object.values(users);
        }

        return [];
    }

    private resolveHasNext(hasNext: any, itemsLength: number): boolean {
        const hasExplicitHasNext = typeof hasNext === 'boolean';

        if (hasExplicitHasNext) {
            return hasNext || itemsLength > this.maxPerPage;
        }

        return itemsLength >= this.maxPerPage;
    }
}
